import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useReadContract } from 'wagmi';
import { COHORT_ABI } from '../contract/abi';
import { DistributionView } from '../components/DistributionView';
import { DEFAULT_MULTIPLIERS } from '../components/DistributionPreview';
import { addToCohortHistory } from '../utils/cohortHistory';

// Validate cohort ID format (0x + 64 hex chars)
function isValidCohortId(id: string): id is `0x${string}` {
  return /^0x[a-fA-F0-9]{64}$/.test(id);
}

export function DistributePage() {
  const { cohortId } = useParams<{ cohortId: string }>();
  const navigate = useNavigate();
  const [cohortTitle, setCohortTitle] = useState<string | null>(null);
  const [multipliers, setMultipliers] = useState(DEFAULT_MULTIPLIERS);

  const contractAddress = import.meta.env.VITE_CONTRACT_ADDRESS as `0x${string}`;

  // Validate cohort ID format
  const validCohortId = cohortId && isValidCohortId(cohortId) ? cohortId : null;

  // Check if cohort is finalized
  const { data: isFinalized, isLoading: isLoadingFinalized } = useReadContract({
    address: contractAddress,
    abi: COHORT_ABI,
    functionName: 'finalized',
    args: validCohortId ? [validCohortId] : undefined,
    query: { enabled: !!validCohortId },
  });

  // Look up cohort title from on-chain registry
  const { data: onChainTitle, isLoading: isLoadingTitle } = useReadContract({
    address: contractAddress,
    abi: COHORT_ABI,
    functionName: 'names',
    args: validCohortId ? [validCohortId] : undefined,
    query: { enabled: !!validCohortId },
  });

  const isLoading = isLoadingFinalized || isLoadingTitle;

  // Set title once loaded
  useEffect(() => {
    if (onChainTitle && typeof onChainTitle === 'string' && onChainTitle.length > 0) {
      setCohortTitle(onChainTitle);
    }
  }, [onChainTitle]);

  // Add to history when we have both cohort ID and title
  useEffect(() => {
    if (validCohortId && cohortTitle) {
      addToCohortHistory(cohortTitle, validCohortId);
    }
  }, [validCohortId, cohortTitle]);

  // Redirect to home if invalid cohort ID format
  useEffect(() => {
    if (cohortId && !validCohortId) {
      navigate('/', { replace: true });
    }
  }, [cohortId, validCohortId, navigate]);

  // Redirect to cohort page if not finalized
  useEffect(() => {
    if (!isLoading && validCohortId && isFinalized === false) {
      navigate(`/${validCohortId}`, { replace: true });
    }
  }, [isLoading, validCohortId, isFinalized, navigate]);

  // Show loading state
  if (isLoading || !validCohortId) {
    return (
      <div className="container">
        <div className="card">
          <p className="text-muted">Loading distribution...</p>
        </div>
      </div>
    );
  }

  // Don't render if not finalized (will redirect)
  if (!isFinalized) {
    return (
      <div className="container">
        <div className="card">
          <p className="text-muted">Cohort not finalized. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <DistributionView
      cohortId={validCohortId}
      cohortTitle={cohortTitle || `Cohort ${validCohortId.slice(0, 10)}...`}
      contractAddress={contractAddress}
      multipliers={multipliers}
      onBack={() => navigate(`/${validCohortId}`)}
      onMultipliersChange={setMultipliers}
    />
  );
}
